import { Box, type SxProps, type Theme } from '@mui/material';
import { color } from '@/colors';
import type { ReactNode } from 'react';

const rootSx: SxProps<Theme> = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
};

export type AppTableColumnWidth = string | number | undefined;

function colStyle(width: AppTableColumnWidth): React.CSSProperties | undefined {
  if (width === undefined) {
    return undefined;
  }
  return typeof width === 'number' ? { width } : { width };
}

type AppTableRootProps = {
  columnWidths: AppTableColumnWidth[];
  children: ReactNode;
  sx?: SxProps<Theme>;
};

const AppTableRoot = ({ columnWidths, children, sx }: AppTableRootProps) => (
  <Box component="table" sx={(sx ? [rootSx, sx] : rootSx) as SxProps<Theme>}>
    <colgroup>
      {columnWidths.map((w, i) => (
        <col key={i} style={colStyle(w)} />
      ))}
    </colgroup>
    {children}
  </Box>
);

const AppTableThead = ({ children }: { children: ReactNode }) => <Box component="thead">{children}</Box>;

const AppTableTbody = ({ children }: { children: ReactNode }) => <Box component="tbody">{children}</Box>;

type AppTableTrProps = {
  children: ReactNode;
  /** Subtle row background on hover (e.g. data tables). */
  hoverable?: boolean;
  sx?: SxProps<Theme>;
};

const AppTableTr = ({ children, hoverable = false, sx }: AppTableTrProps) => (
  <Box
    component="tr"
    sx={
      [
        hoverable
          ? {
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: color.White04_T },
            }
          : {},
        sx ?? {},
      ] as SxProps<Theme>
    }
  >
    {children}
  </Box>
);

type AppTableThProps = {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  scope?: 'col' | 'row';
  /** Marks the header cell as presentational only (e.g. empty actions column). */
  ariaHidden?: boolean;
  sx?: SxProps<Theme>;
};

const headerCellSx: SxProps<Theme> = {
  textAlign: 'left',
  fontWeight: 400,
  pb: 1,
  pr: 2,
  borderBottom: `1px solid ${color.White08_T}`,
  verticalAlign: 'bottom',
};

const AppTableTh = ({ children, align = 'left', scope = 'col', ariaHidden, sx }: AppTableThProps) => (
  <Box
    component="th"
    scope={ariaHidden ? undefined : scope}
    aria-hidden={ariaHidden ? true : undefined}
    sx={[headerCellSx, align !== 'left' ? { textAlign: align } : {}, sx ?? {}] as SxProps<Theme>}
  >
    {children}
  </Box>
);

type AppTableTdProps = {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  sx?: SxProps<Theme>;
};

const bodyCellSx: SxProps<Theme> = {
  py: 2,
  pr: 2,
  borderBottom: `1px solid ${color.White08_T}`,
  verticalAlign: 'middle',
};

const AppTableTd = ({ children, align = 'left', verticalAlign = 'middle', sx }: AppTableTdProps) => (
  <Box
    component="td"
    sx={[bodyCellSx, { verticalAlign }, align !== 'left' ? { textAlign: align } : {}, sx ?? {}] as SxProps<Theme>}
  >
    {children}
  </Box>
);

export const AppTable = Object.assign(AppTableRoot, {
  Thead: AppTableThead,
  Tbody: AppTableTbody,
  Tr: AppTableTr,
  Th: AppTableTh,
  Td: AppTableTd,
});
